import type { Task } from '@portfolio-pm/shared';
import { useEffect, useMemo, useState } from 'react';
import { useNow } from '../../lib/useNow.ts';
import {
  addDays,
  defaultScheduledAt,
  formatDayLabel,
  formatMonthLabel,
  formatShortDate,
  formatTaskDate,
  formatTimeLabel,
  isSameDayLocal,
  moveDatePreservingTime,
  startOfDayLocal,
  startOfMonth,
  startOfMonthGrid,
  toDayKey,
} from './task-view-dates.ts';
import { getTaskCalendarAnchor } from './task-workspace-model.ts';

const DRAG_MIME = 'application/x-portfolio-task';

export function CalendarView({
  tasks,
  selectedTaskId,
  onSelectTask,
  onRescheduleTask,
}: {
  tasks: Task[];
  selectedTaskId: string | undefined;
  onSelectTask(taskId: string): void;
  onRescheduleTask(taskId: string, targetDay: number): void;
}): JSX.Element {
  const now = useNow();
  const [monthStart, setMonthStart] = useState(() => startOfMonth(now));
  const [selectedDay, setSelectedDay] = useState(() => startOfDayLocal(now));

  const datedTasks = useMemo(
    () =>
      tasks
        .map((task) => ({ task, anchor: getTaskCalendarAnchor(task) }))
        .filter((entry): entry is { task: Task; anchor: number } => entry.anchor !== undefined)
        .sort((left, right) => left.anchor - right.anchor),
    [tasks],
  );

  useEffect(() => {
    const selectedTask = datedTasks.find((entry) => entry.task.id === selectedTaskId);
    if (!selectedTask) return;
    setSelectedDay(startOfDayLocal(selectedTask.anchor));
    setMonthStart(startOfMonth(selectedTask.anchor));
  }, [datedTasks, selectedTaskId]);

  const days = useMemo(() => {
    const start = startOfMonthGrid(monthStart);
    return Array.from({ length: 42 }, (_, index) => addDays(start, index));
  }, [monthStart]);

  const tasksByDay = useMemo(() => {
    const grouped = new Map<string, Task[]>();
    for (const { task, anchor } of datedTasks) {
      const key = toDayKey(anchor);
      const bucket = grouped.get(key) ?? [];
      bucket.push(task);
      grouped.set(key, bucket);
    }
    return grouped;
  }, [datedTasks]);

  const selectedDayTasks = tasksByDay.get(toDayKey(selectedDay)) ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow-label">Calendar</p>
          <h3 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
            {formatMonthLabel(monthStart)}
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMonthStart((value) => startOfMonth(addDays(value, -28)))}
            className="ui-button ui-button-subtle"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => {
              setMonthStart(startOfMonth(now));
              setSelectedDay(startOfDayLocal(now));
            }}
            className="ui-button"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setMonthStart((value) => startOfMonth(addDays(value, 35)))}
            className="ui-button ui-button-subtle"
          >
            Next
          </button>
        </div>
      </div>

      <div className="surface-panel grid min-h-0 flex-1 gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="min-h-0">
          <div className="grid grid-cols-7 gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--app-text-soft)]">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="px-2 py-1">
                {label}
              </div>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-7 gap-2">
            {days.map((day) => {
              const dayTasks = tasksByDay.get(toDayKey(day)) ?? [];
              const inMonth = new Date(day).getMonth() === new Date(monthStart).getMonth();
              const selected = isSameDayLocal(day, selectedDay);
              return (
                <button
                  key={day}
                  type="button"
                  data-testid="calendar-day-cell"
                  data-day={toDayKey(day)}
                  onClick={() => setSelectedDay(day)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    const taskId =
                      event.dataTransfer.getData(DRAG_MIME) ||
                      event.dataTransfer.getData('text/plain');
                    if (!taskId) return;
                    event.preventDefault();
                    setSelectedDay(day);
                    onRescheduleTask(taskId, day);
                    onSelectTask(taskId);
                  }}
                  className={`flex min-h-[8rem] flex-col rounded-2xl border p-2 text-left transition ${
                    selected
                      ? 'border-[color:var(--app-border-strong)] bg-[color:var(--app-accent-soft)]'
                      : 'border-[color:var(--app-border)] bg-white/70 hover:bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`text-sm font-semibold ${
                        inMonth
                          ? 'text-[color:var(--app-text)]'
                          : 'text-[color:var(--app-text-soft)]'
                      }`}
                    >
                      {new Date(day).getDate()}
                    </span>
                    {dayTasks.length > 0 && (
                      <span className="ui-chip px-2 py-0.5 text-[11px]">{dayTasks.length}</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-1 flex-col gap-1">
                    {dayTasks.slice(0, 3).map((task) => (
                      <span
                        key={task.id}
                        draggable
                        data-testid="calendar-task-pill"
                        data-task-id={task.id}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move';
                          event.dataTransfer.setData(DRAG_MIME, task.id);
                          event.dataTransfer.setData('text/plain', task.id);
                        }}
                        className={`truncate rounded-lg px-2 py-1 text-left text-xs ${
                          task.id === selectedTaskId
                            ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-accent-text)]'
                            : 'bg-[color:var(--app-surface-wash-strong)] text-[color:var(--app-text-muted)]'
                        }`}
                      >
                        {task.title}
                      </span>
                    ))}
                    {dayTasks.length > 3 && (
                      <span className="px-2 text-[11px] text-[color:var(--app-text-soft)]">
                        +{dayTasks.length - 3} more
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="surface-panel-muted flex min-h-0 flex-col p-4">
          <p className="eyebrow-label">Selected Day</p>
          <h4 className="mt-1 text-base font-semibold text-[color:var(--app-text)]">
            {formatDayLabel(selectedDay)}
          </h4>
          <div
            data-testid="calendar-agenda"
            className="mt-4 min-h-0 flex-1 space-y-3 overflow-auto pr-1"
          >
            {selectedDayTasks.length === 0 ? (
              <div className="surface-empty p-4 text-sm">
                No dated tasks for this day yet. Drag a task here from another day to reschedule it.
              </div>
            ) : (
              selectedDayTasks.map((task) => {
                const anchor = getTaskCalendarAnchor(task);
                return (
                  <button
                    key={task.id}
                    type="button"
                    data-testid="calendar-agenda-item"
                    onClick={() => onSelectTask(task.id)}
                    className={`surface-panel-muted w-full p-3 text-left ${
                      task.id === selectedTaskId ? 'ring-2 ring-[rgba(185,108,45,0.24)]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-[color:var(--app-text)]">
                          {task.title}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-[color:var(--app-text-muted)]">
                          {anchor !== undefined &&
                            (task.scheduledAt !== undefined
                              ? task.scheduledAtHasTime
                              : task.dueAtHasTime) && <span>{formatTimeLabel(anchor)}</span>}
                          {task.scheduledAt !== undefined && task.dueAt !== undefined && (
                            <span>Due {formatTaskDate(task.dueAt, task.dueAtHasTime)}</span>
                          )}
                          {task.scheduledAt === undefined && task.dueAt !== undefined && (
                            <span>Due {formatTaskDate(task.dueAt, task.dueAtHasTime)}</span>
                          )}
                          {task.reminderAt !== undefined && (
                            <span>Remind {formatShortDate(task.reminderAt)}</span>
                          )}
                        </div>
                      </div>
                      {task.priority > 0 && (
                        <span className="ui-chip ui-chip-accent">P{task.priority}</span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export function rescheduleTaskScheduledAt(task: Task, targetDay: number): number {
  if (task.scheduledAt !== undefined) {
    return moveDatePreservingTime(task.scheduledAt, targetDay);
  }
  return defaultScheduledAt(targetDay);
}
